'use client';

import { useActionState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { signIn, signInWithGoogle } from '@/lib/actions/auth';
import { useTranslateActionError } from '@/components/ui/action-error';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { GoogleIcon } from '@/components/ui/google-icon';
import styles from '../auth.module.css';

interface Props {
  passwordUpdated: boolean;
  callbackError?: string;
}

export function LoginForm({ passwordUpdated, callbackError }: Props) {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const tErr = useTranslateActionError();
  const [state, action, isPending] = useActionState(signIn, null);

  async function handleGoogleSignIn() {
    const result = await signInWithGoogle();
    if ('url' in result) {
      window.location.assign(result.url);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <span className={styles.logoText}>{tc('appName')}</span>
        </div>

        <h1 className={styles.heading}>{t('welcomeBack')}</h1>
        <p className={styles.subheading}>{t('signInToYourAccount')}</p>

        {passwordUpdated && (
          <div className={styles.successBanner} role="status">
            {t('passwordUpdatedBanner')}
          </div>
        )}

        {callbackError && (
          <div className={styles.error} role="alert">
            {callbackError === 'auth_failed'
              ? t('errors.authFailed')
              : callbackError === 'invalid_callback'
              ? t('errors.invalidCallback')
              : t('errors.unexpected')}
          </div>
        )}

        {state && !state.success && (
          <div className={styles.error} role="alert">
            {tErr(state.error)}
          </div>
        )}

        <form action={action} className={styles.form}>
          <Field label={t('email')} required>
            <Input
              type="email"
              name="email"
              placeholder={t('emailPlaceholder')}
              autoComplete="email"
            />
          </Field>
          <div>
            <Field label={t('password')} required>
              <Input
                type="password"
                name="password"
                placeholder={t('passwordPlaceholder')}
                autoComplete="current-password"
              />
            </Field>
            <div className={styles.forgotLinkWrapper}>
              <Link href="/auth/reset-password" className={styles.forgotLink}>
                {t('forgotPassword')}
              </Link>
            </div>
          </div>
          <Button type="submit" fullWidth loading={isPending}>
            {t('signIn')}
          </Button>
        </form>

        <div className={styles.divider}>{t('orContinueWith')}</div>

        <Button
          type="button"
          variant="secondary"
          fullWidth
          onClick={handleGoogleSignIn}
        >
          <span className={styles.googleButtonContent}>
            <GoogleIcon className={styles.googleIcon} />
            {t('continueWithGoogle')}
          </span>
        </Button>

        <div className={styles.links}>
          <span>
            {t('noAccount')}{' '}
            <Link href="/auth/signup">{t('signUpLink')}</Link>
          </span>
        </div>
      </div>
    </div>
  );
}
